import React, { useState, useEffect, useCallback } from 'react';
import TradingViewWidget from '../../components/TradingViewWidget';
import { externalApi } from '../../services/api';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { formatINR } from '../../utils/formatters';

const REC_COLORS = {
  STRONG_BUY: '#10B981',
  BUY: '#22C55E',
  NEUTRAL: '#94A3B8',
  SELL: '#F97316',
  STRONG_SELL: '#EF4444',
};

const INTERVALS = ['1m', '5m', '15m', '1h', '4h', '1D', '1W'];

const INDICATOR_ROWS = [
  { key: 'close', label: 'Close', formatter: formatINR },
  { key: 'change', label: 'Change', formatter: (v) => (v ? `${v.toFixed(2)}%` : '—') },
  { key: 'RSI', label: 'RSI(14)', formatter: (v) => v?.toFixed(2) },
  { key: 'MACD.macd', label: 'MACD', formatter: (v) => v?.toFixed(2) },
  { key: 'EMA20', label: 'EMA20', formatter: formatINR },
  { key: 'EMA50', label: 'EMA50', formatter: formatINR },
  { key: 'EMA200', label: 'EMA200', formatter: formatINR },
  { key: 'SMA200', label: 'SMA200', formatter: formatINR },
  { key: 'ADX', label: 'ADX', formatter: (v) => v?.toFixed(2) },
  { key: 'Stoch.K', label: 'Stoch.K', formatter: (v) => v?.toFixed(2) },
  { key: 'BB.upper', label: 'BB Upper', formatter: formatINR },
  { key: 'BB.lower', label: 'BB Lower', formatter: formatINR },
];

export default function TradingViewTab() {
  const [symbol, setSymbol] = useState('RELIANCE');
  const [query, setQuery] = useState('RELIANCE');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  const fetchData = useCallback(async (s) => {
    setLoading(true);
    try {
      const resp = await externalApi.tradingview(s);
      setData(resp.data);
    } catch (e) {
      console.error('tradingview fetch failed', e);
      setData({ error: 'Failed to fetch TradingView data' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(symbol); }, [symbol, fetchData]);

  const intervals = data?.intervals || {};
  const ind = data?.indicators || {};
  const hasIndicators = Object.keys(ind).length > 0;

  return (
    <div className="space-y-6" data-testid="tradingview-tab-content">
      <div className="flex gap-2 max-w-md">
        <Input
          placeholder="Enter NSE symbol (e.g. RELIANCE)"
          value={query}
          onChange={(e) => setQuery(e.target.value.toUpperCase())}
          onKeyDown={(e) => e.key === 'Enter' && setSymbol(query.trim())}
          className="bg-[#0A0B0E] border-white/20 text-white"
          data-testid="tv-input"
        />
        <Button onClick={() => setSymbol(query.trim())} className="bg-[#2563EB] hover:bg-[#1D4ED8]" data-testid="tv-fetch-btn">
          Fetch
        </Button>
      </div>

      <RatingGrid loading={loading} symbol={symbol} intervals={intervals} />

      {!loading && hasIndicators && <IndicatorSnapshot indicators={ind} />}

      <div>
        <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">Live TradingView Chart</p>
        <div className="border border-white/10">
          <TradingViewWidget symbol={symbol} exchange="NSE" height={520} />
        </div>
      </div>
    </div>
  );
}

function RatingGrid({ loading, symbol, intervals }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">
        TradingView Technical Rating • {symbol} (NSE)
      </p>
      {loading && <div className="text-[#64748B] text-sm">Loading analysis…</div>}
      {!loading && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          {INTERVALS.map((iv) => (
            <IntervalCard key={iv} interval={iv} rec={intervals[iv]} />
          ))}
        </div>
      )}
    </div>
  );
}

function IntervalCard({ interval, rec }) {
  const rating = rec?.recommendation || 'N/A';
  const color = REC_COLORS[rating] || '#94A3B8';
  return (
    <div className="p-3 border border-white/10 bg-white/[0.02]" data-testid={`tv-interval-${interval}`}>
      <div className="text-[10px] text-[#64748B] mb-1">{interval}</div>
      <div className="text-xs font-bold mb-2" style={{ color }}>
        {rating.replace('_', ' ')}
      </div>
      {rec?.buy != null && (
        <div className="text-[10px] text-[#94A3B8] space-y-0.5">
          <div className="flex justify-between"><span>Buy</span><span className="text-[#10B981]">{rec.buy}</span></div>
          <div className="flex justify-between"><span>Sell</span><span className="text-[#EF4444]">{rec.sell}</span></div>
          <div className="flex justify-between"><span>Neutral</span><span>{rec.neutral}</span></div>
        </div>
      )}
    </div>
  );
}

function IndicatorSnapshot({ indicators }) {
  const format = (row) => {
    const val = indicators[row.key];
    if (val == null || val === '') return '—';
    return typeof row.formatter === 'function' ? row.formatter(val) : val;
  };
  return (
    <div>
      <p className="text-[10px] uppercase tracking-[0.2em] text-[#64748B] mb-3">Daily Indicator Snapshot</p>
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-2">
        {INDICATOR_ROWS.map((row) => (
          <div key={row.key} className="p-2 border border-white/10 bg-white/[0.02]">
            <div className="text-[10px] text-[#64748B] uppercase">{row.label}</div>
            <div className="text-sm font-mono text-white mt-0.5">{format(row)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
